import { cn } from '@heidi/utils';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { objectWorkspaceMessages as messages } from './ObjectWorkspace.messages';
import { ObjectWorkspacePane } from './ObjectWorkspacePane';
import type {
  ObjectWorkspaceAnchor,
  ObjectWorkspaceAnchorSelection,
  ObjectWorkspaceFacet,
  ObjectWorkspaceObject,
  ObjectWorkspacePatientDetailsState,
  ObjectWorkspaceWorkEngagement,
  ObjectWorkspaceWorkSelection,
} from './objectWorkspace.data';

export interface ObjectWorkspaceWorkbenchContext {
  object: ObjectWorkspaceObject;
  facet: ObjectWorkspaceFacet;
  anchorSelection?: ObjectWorkspaceAnchorSelection;
  workSelection?: ObjectWorkspaceWorkSelection;
  newTabTitle: string;
}

export interface ObjectWorkspaceShellProps {
  /** Real identity supplied by the patient or visit selection entry point. */
  object: ObjectWorkspaceObject;
  /** Wait for the owning route to replace `object` before changing identity. */
  isObjectControlled?: boolean;
  /** Optional app-owned workbench content. */
  children?: ReactNode;
  /**
   * App-resolved patient profile state. Required for patient Details; omit on
   * visits (no Details facet) or while the host has nothing to show.
   */
  patientDetailsState?: ObjectWorkspacePatientDetailsState;
  /**
   * Host-owned object summary. Omit (or pass '') for an empty editor — the shell
   * never invents clinical prototype copy.
   */
  summary?: string;
  /** App-owned Work chat composition. Takes precedence over `children`. */
  renderWorkbench?: (context: ObjectWorkspaceWorkbenchContext) => ReactNode;
  className?: string;
  onClose: () => void;
  onObjectChange?: (object: ObjectWorkspaceObject) => void;
  /** Opens the host-owned patient attachment flow when an encounter has no patient. */
  onAddPatient?: (anchorElement: HTMLElement) => void;
  /** Whether an explicit user action currently owns the context panel. */
  isContextPanelOpen?: boolean;
  /** Closes context before restoring the full object pane. */
  onObjectPaneExpand?: () => void;
  /** Same-kind peers from the mounted Home data source (empty when alone). */
  switcherOptions: readonly ObjectWorkspaceObject[];
  onAnchorSelect?: (anchor: ObjectWorkspaceAnchor) => void;
  onMoreActions?: () => void;
}

type ObjectWorkspaceContentProps = Omit<
  ObjectWorkspaceShellProps,
  'isObjectControlled' | 'onObjectChange'
> & {
  onObjectChange: (object: ObjectWorkspaceObject) => void;
};

/**
 * Persistent patient/visit workspace chrome. The primary navigation rail stays
 * app-owned immediately outside this component; this shell occupies the Home
 * content slot beside it and keeps the object pane fixed next to a flexible
 * workbench surface.
 */
function ObjectWorkspaceContent({
  object,
  children,
  patientDetailsState,
  summary,
  renderWorkbench,
  className,
  onClose,
  onObjectChange,
  onAddPatient,
  isContextPanelOpen = false,
  onObjectPaneExpand,
  switcherOptions: suppliedSwitcherOptions,
  onAnchorSelect,
  onMoreActions,
}: ObjectWorkspaceContentProps): JSX.Element {
  const intl = useIntl();
  const [activeFacet, setActiveFacet] = useState<ObjectWorkspaceFacet>('work');
  const [anchorSelection, setAnchorSelection] =
    useState<ObjectWorkspaceAnchorSelection>();
  const [workSelection, setWorkSelection] = useState<ObjectWorkspaceWorkSelection>();

  const switcherOptions = useMemo(
    () => [
      object,
      ...suppliedSwitcherOptions.filter((option) => option.id !== object.id),
    ],
    [object, suppliedSwitcherOptions]
  );

  const handleObjectChange = (nextObject: ObjectWorkspaceObject) => {
    onObjectChange(nextObject);
  };

  const handleAnchorSelect = (anchor: ObjectWorkspaceAnchor) => {
    setAnchorSelection((current) => ({
      anchor,
      prompt: intl.formatMessage(
        anchor.kind === 'session'
          ? messages.pullUpRecentSession
          : messages.pullUpDosageChange
      ),
      sequence: (current?.sequence ?? 0) + 1,
    }));
    onAnchorSelect?.(anchor);
  };

  const handleWorkEngage = (work: ObjectWorkspaceWorkEngagement) => {
    setWorkSelection((current) => ({
      work,
      sequence: (current?.sequence ?? 0) + 1,
    }));
  };

  return (
    <section
      aria-label={intl.formatMessage(messages.workspaceLabel, {
        name: object.name,
      })}
      className={cn(
        'flex size-full min-h-0 overflow-hidden bg-surface-accent md:gap-4 md:p-4',
        className
      )}
      data-testid="object-workspace-shell"
    >
      <ObjectWorkspacePane
        object={object}
        patientDetailsState={object.kind === 'patient' ? patientDetailsState : undefined}
        summary={summary}
        isCollapsed={isContextPanelOpen}
        onExpand={onObjectPaneExpand}
        switcherOptions={switcherOptions}
        onClose={onClose}
        onObjectChange={handleObjectChange}
        onAddPatient={onAddPatient}
        onFacetChange={setActiveFacet}
        onAnchorSelect={handleAnchorSelect}
        onWorkEngage={handleWorkEngage}
        onMoreActions={onMoreActions}
      />
      <div
        aria-label={intl.formatMessage(messages.workbenchLabel, {
          name: object.name,
        })}
        className="hidden min-w-0 flex-1 overflow-hidden md:block"
        data-testid="object-workspace-workbench"
      >
        {renderWorkbench
          ? renderWorkbench({
              object,
              facet: activeFacet,
              anchorSelection,
              workSelection,
              newTabTitle: intl.formatMessage(messages.newThread),
            })
          : children}
      </div>
    </section>
  );
}

export function ObjectWorkspaceShell({
  object,
  isObjectControlled = false,
  onObjectChange,
  ...contentProps
}: ObjectWorkspaceShellProps): JSX.Element {
  const [uncontrolledObject, setUncontrolledObject] = useState(object);

  useEffect(() => {
    setUncontrolledObject(object);
  }, [object]);

  const activeObject = isObjectControlled ? object : uncontrolledObject;
  const handleObjectChange = (nextObject: ObjectWorkspaceObject) => {
    if (!isObjectControlled) setUncontrolledObject(nextObject);
    onObjectChange?.(nextObject);
  };

  return (
    <ObjectWorkspaceContent
      key={`${activeObject.kind}:${activeObject.id}`}
      {...contentProps}
      object={activeObject}
      onObjectChange={handleObjectChange}
    />
  );
}

export type {
  ObjectWorkspaceAnchor,
  ObjectWorkspaceAnchorSelection,
  ObjectWorkspaceFacet,
  ObjectWorkspaceKind,
  ObjectWorkspaceObject,
  ObjectWorkspacePatientDetails,
  ObjectWorkspacePatientDetailsState,
  ObjectWorkspaceWorkSelection,
} from './objectWorkspace.data';
